import { useFrame } from '@react-three/fiber';
import { useState, useRef, useEffect, useMemo } from 'react';
import { Html, useCursor } from '@react-three/drei';
import * as THREE from 'three';
import { a, config, useSpring as useThreeSpring } from '@react-spring/three';
import { a as aWeb, useSpring as useWebSpring } from '@react-spring/web';
import { InteractiveCursorIcon } from './InteractiveCursorIcon';
import { useMutableCallback } from '@slope/uikit';
import { useGuardClick } from '@slope/hooks';

const CursorIcon = aWeb(InteractiveCursorIcon);

export enum InteractiveHighlight {
  None = 0x0,
  Hovered = 0x1,
  Near = 0x2,
  Far = 0x4,
}

export const Interactive = ({
  object,
  onClick,
  visible = true,
  highlightMask = InteractiveHighlight.Hovered |
    InteractiveHighlight.Near |
    InteractiveHighlight.Far,
  iconMask = InteractiveHighlight.Near | InteractiveHighlight.Hovered,
  distanceFactor: distanceFactorProp,
  highlightOpacity: highlightOpacityProp = [0.3, 0.7],
}: {
  object: THREE.Object3D;
  onClick: () => void;
  visible?: boolean;
  highlightMask?: number;
  iconMask?: number;
  distanceFactor?: number;
  highlightOpacity?: number | [number, number];
}) => {
  const [mask, setMask] = useState(0);
  const meshRef = useRef<THREE.Mesh>(null);

  const distanceFactor = useMemo(
    () =>
      distanceFactorProp ??
      new THREE.Box3().setFromObject(object).getSize(new THREE.Vector3()).length() * 2,
    [object, distanceFactorProp],
  );

  const highlightOpacity = useMemo(
    () =>
      Array.isArray(highlightOpacityProp)
        ? highlightOpacityProp
        : [highlightOpacityProp, highlightOpacityProp],
    [object, highlightOpacityProp],
  );

  useCursor(visible && !!(mask & InteractiveHighlight.Hovered), 'pointer');

  useFrame(({ camera }) => {
    if (highlightMask & InteractiveHighlight.Near) {
      const isNear = camera.position.distanceTo(object.position) < distanceFactor;
      let targetMask = mask;

      if (isNear) {
        targetMask = (mask & ~InteractiveHighlight.Far) | InteractiveHighlight.Near;
      } else {
        targetMask = mask & ~InteractiveHighlight.Near;
        // Only set Far flag if it's included in the highlightMask
        if (highlightMask & InteractiveHighlight.Far) {
          targetMask = targetMask | InteractiveHighlight.Far;
        }
      }

      if (targetMask !== mask) setMask(targetMask);
    }
  });

  const [springProps, api] = useThreeSpring(() => ({ opacity: 0 }));

  useEffect(() => {
    if (mask & InteractiveHighlight.Hovered) {
      api.stop();
      api.start({
        opacity: highlightOpacity[1],
        config: config.stiff,
      });
    } else if (mask & (InteractiveHighlight.Near | InteractiveHighlight.Far)) {
      api.stop();
      api.start({
        to: async (next) => {
          while (true) {
            await next({ opacity: highlightOpacity[1] });
            await next({ opacity: highlightOpacity[0] });
          }
        },
        loop: { reverse: true },
        config: config.wobbly,
      });
    } else {
      api.stop();
      api.start({ opacity: highlightOpacity[0], config: config.stiff });
    }

    return () => {
      api.stop();
    };
  }, [mask, api]);

  const iconSpring = useWebSpring({
    transform: mask & iconMask ? `scale(1) translate(10, 0)` : 'scale(1) translate(0, 0)',
    opacity: mask & iconMask ? 1 : 0,
    config: config.wobbly,
    loop: {
      from: {
        transform: 'scale(1)',
      },
      to: {
        transform: 'scale(0.7)',
      },
      reverse: true,
    },
  });

  const handlePointerOver = useMutableCallback(() => {
    setMask((prev) => prev | InteractiveHighlight.Hovered);
  });

  const handlePointerOut = useMutableCallback(() => {
    setMask((prev) => prev & ~InteractiveHighlight.Hovered);
  });

  const hoverProps = {
    onPointerOver: handlePointerOver,
    onPointerOut: handlePointerOut,
  };

  const guardClickProps = useGuardClick(onClick);

  return (
    <mesh
      ref={meshRef}
      name="interactive"
      geometry={(object as THREE.Mesh).geometry}
      matrixWorld={object.matrixWorld}
      matrixWorldAutoUpdate={false}
      {...hoverProps}
      {...(visible && guardClickProps)}
      visible={visible}
    >
      <a.meshBasicMaterial transparent {...springProps} color="white" depthTest={false} />

      {visible && (
        <Html
          position={[0, 0, 0]}
          center
          distanceFactor={distanceFactor / 10}
          style={{
            cursor: 'pointer',
          }}
        >
          <CursorIcon style={iconSpring} onClick={visible && onClick} />
        </Html>
      )}
    </mesh>
  );
};
